<script lang="ts">
  import CoordinatorWorkspace from './CoordinatorWorkspace.svelte';
  import OwnedSalesWork from './OwnedSalesWork.svelte';
  import DailyMeetings from './DailyMeetings.svelte';
  import HandoverPanel from './HandoverPanel.svelte';
  import CallQueueCard from './CallQueueCard.svelte';
  import CustomerCareDashboard from './CustomerCareDashboard.svelte';
  import { salesQueries } from './salesQueries';
  import { callQueries } from './coordinatorQueries';
  import { roleProfile, rolePresentation, defaultDayScope, workflowAccessKey } from '$lib/utils/roleProfile';
  import { canUseHappyQueue } from '$lib/utils/happyCode';
  import { onMount, untrack } from 'svelte';
  import { createQuery } from '@tanstack/svelte-query';
  import { activitiesApi } from '$lib/api/crm';
  import { touchpointsApi } from '$lib/api/touchpoints';
  import { leadsApi } from '$lib/api/leads';
  import { customerCareApi } from '$lib/api/customerCare';
  import { getErrorMessage } from '$lib/api/client';
  import { session } from '$lib/state/session.svelte';
  import { route } from '$lib/state/route.svelte';
  import Composer from './Composer.svelte';
  import { shortDay, touchpointTypeLabel } from '$lib/utils/touchpoints';
  import { dayInIndia, dueWork, manualActivityTypes, mayCompleteOwnTouchpoint, stalledCaseReminders, readQueuePages, todaysMeetings, todaysAgenda, nextUp, dayProgress, greetingInIndia, timeInIndia, minutesUntil, untilLabel, daysSinceInIndia, type DayScope, type DueWork } from '$lib/utils/myDay';

  const profile = $derived(roleProfile(session.me));
  const accessKey = $derived(workflowAccessKey(session.me));
  let scopeChoice = $state<{ identity: string; scope: DayScope } | null>(null);
  const identity = $derived(`${session.me?.user.id}:${profile}`);
  const scope = $derived(scopeChoice?.identity === identity ? scopeChoice.scope : defaultDayScope(profile));
  let now = $state(new Date());
  let completing = $state<DueWork | null>(null);
  const enabled = $derived(profile !== 'unavailable');
  const crmEnabled = $derived(enabled && (session.isAdmin || !!session.me?.verticals.length));
  const vertical = $derived(session.selectedVertical);
  const verticalId = $derived(vertical === 'all' ? undefined : vertical);
  const completionContext = $derived(`${identity}:${vertical}:${accessKey}`);
  const userId = $derived(session.me?.user.id);
  const today = $derived(dayInIndia(now.toISOString())!);
  const scopeName = $derived(vertical === 'all' ? 'All accessible CRM teams' : session.me?.verticals.find(team => team.id === vertical)?.name || 'Selected team');
  const firstName = $derived((session.me?.user.name || '').trim().split(/\s+/)[0] || 'there');
  const happyAccess = $derived(canUseHappyQueue(session.me?.user, session.me?.happy_code_queue_access ?? false, session.me?.verticals));
  onMount(() => { const timer = setInterval(() => now = new Date(), 60000); return () => clearInterval(timer); });

  const activities = createQuery(() => ({
    queryKey: ['my-day-action-activities', profile, userId, vertical, accessKey], enabled: crmEnabled,
    queryFn: async () => {
      const requestedVertical = verticalId;
      // No unsupported due/assignee filters: those would silently be ignored by
      // the current endpoint. Exclude synced emails at source by requesting types.
      const groups = await Promise.all(manualActivityTypes.map(type => readQueuePages(page => activitiesApi.list({ type, vertical_id: requestedVertical, page, per_page: 100 }))));
      return groups.flat();
    }, staleTime: 60000, refetchOnWindowFocus: true
  }));
  // Keep history for progress/meeting history, but use the server's latest-
  // visible-touchpoint predicate for due queues (including accessible emails).
  const agendaRows = createQuery(() => ({
    queryKey: ['my-day-agenda', profile, userId, vertical, accessKey], enabled: crmEnabled,
    queryFn: () => readQueuePages(page => touchpointsApi.list({ view:'agenda', scope:'team', vertical_id:verticalId, page, per_page:100 })),
    staleTime:60000, refetchOnWindowFocus:true
  }));
  const leads = createQuery(() => ({
    queryKey: ['my-day-action-leads', profile, userId, vertical, accessKey], enabled: crmEnabled && profile === 'coordination',
    queryFn: () => leadsApi.list({ vertical_id: verticalId, status: 'new', spam: 'false', exclude_download_only: 'true', per_page: 5, page: 1 }),
    staleTime: 60000, refetchOnWindowFocus: true
  }));
  const care = createQuery(() => ({
    queryKey: ['my-day-action-care', profile, userId, vertical, accessKey], enabled: enabled && profile !== 'sales',
    queryFn: () => customerCareApi.list('open_issues', 1, profile === 'care' ? null : verticalId ?? null, 3, 30), staleTime: 60000, refetchOnWindowFocus: true
  }));
  // Hero counts reuse the sections' cached, identity-scoped queries.
  const sales = $derived(salesQueries({ profile, userId, vertical, accessKey }));
  const ownedIssues = createQuery(() => sales.issues);
  const hanging = createQuery(() => sales.hanging);
  const calls = $derived(callQueries({ profile, userId, vertical, accessKey, happyAccess }));
  const happy = createQuery(() => calls.happy);
  const fixes = createQuery(() => calls.fixes);
  const callbacks = createQuery(() => calls.callbacks);

  const queues = $derived({
    mine: dueWork(agendaRows.data ?? [], today, 'mine', userId),
    team: dueWork(agendaRows.data ?? [], today, 'team', userId),
    unassigned: dueWork(agendaRows.data ?? [], today, 'unassigned', userId)
  });
  const followups = $derived(queues[scope]);
  const agendaCounts = createQuery(() => ({ queryKey: ['agenda-counts', 'my-day', userId, verticalId], queryFn: () => touchpointsApi.buckets({ scope: 'mine', vertical_id: verticalId }), enabled: crmEnabled }));
  const overdue = $derived(agendaCounts.data?.overdue ?? 0);
  const agenda = $derived(todaysAgenda(activities.data ?? [], today, userId));
  const upcoming = $derived(nextUp(agenda, now));
  const meetingsLeft = $derived(agenda.filter(row => row.status === 'scheduled' && Date.parse(row.scheduled_at!) >= now.getTime()).length);
  const progress = $derived(dayProgress(activities.data ?? [], queues.mine, today, userId));
  const ring = $derived(`conic-gradient(var(--tym-lime) 0deg ${progress.pct * 3.6}deg, #eeedea ${progress.pct * 3.6}deg 360deg)`);
  const bookedToday = $derived(session.me ? todaysMeetings(activities.data ?? [], today, 'booked', session.me.user) : []);
  const quietCases = $derived(stalledCaseReminders(care.data?.open_issues?.results ?? []));
  const callCount = $derived((happy.data?.due_calls?.length ?? 0) + (happy.data?.negatives?.length ?? 0) + (fixes.data?.need_to_speak?.count ?? 0) + (callbacks.data?.contact_calls?.count ?? 0));
  $effect(() => {
    completionContext;
    untrack(() => { completing = null; });
  });
  function startCompletion(work: DueWork) { completing = work; }
  function cancelCompletion() { completing = null; }
  function showDate(work: DueWork) {
    // "Sat, 31 Oct" for a dated reminder; "31 Oct, 3:00 pm" when there is a time.
    return work.kind === 'next_action' || /^\d{4}-\d{2}-\d{2}$/.test(work.date) ? shortDay(work.day) : new Intl.DateTimeFormat('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }).format(new Date(work.date));
  }
  function plural(n: number, one: string, many = `${one}s`) { return `${n} ${n === 1 ? one : many}`; }
  function bookedBy(createdBy: string | undefined) {
    const mine = session.me?.user.email.trim().toLowerCase();
    return createdBy && createdBy.trim().toLowerCase() !== mine ? `Booked by ${createdBy.split('@')[0]}` : '';
  }
  function scrollTo(id: string) { document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
</script>

<div class="tym-page space-y-4 sm:space-y-5">
  <CoordinatorWorkspace />

  <section class="flex flex-wrap items-center justify-between gap-4 sm:gap-6">
    <div class="min-w-0 flex-[1_1_280px] sm:flex-[1_1_420px]">
      <p class="text-xs text-dark-gray sm:text-[13px]">{new Intl.DateTimeFormat('en-IN', { timeZone: 'Asia/Kolkata', weekday: 'long', day: 'numeric', month: 'long' }).format(now)} · {timeInIndia(now.toISOString())} IST · {scopeName}</p>
      <h1 class="mt-1 font-heading text-[26px] font-bold leading-tight tracking-tight text-navy sm:mt-1.5 sm:text-[34px]">{enabled ? `${greetingInIndia(now)}, ${firstName}.` : 'My day'}</h1>
      <p class="mt-1 hidden text-sm font-semibold text-toyama-teal sm:block">{rolePresentation[profile].title}</p>
      {#if profile === 'sales' && activities.isSuccess}
        <p class="mt-2 hidden max-w-[680px] text-base text-toyama-heavy-carbon sm:block">
          {#if overdue || meetingsLeft || ownedIssues.data?.count || hanging.data?.count}
            You have
            {#if overdue}<strong>{plural(overdue, 'overdue follow-up')}</strong>, {/if}
            <strong>{plural(meetingsLeft, 'meeting')}</strong> left today{#if ownedIssues.data?.count}, <strong>{plural(ownedIssues.data.count, 'open issue')}</strong> on your customers{/if}{#if hanging.data?.count}, and <strong>{plural(hanging.data.count, 'B2B account')}</strong> with no next step{/if}.
          {:else}Nothing overdue and no meetings left. A good moment to call a customer you haven't spoken to in a while.{/if}
        </p>
      {:else}<p class="mt-2 hidden max-w-[680px] text-sm text-dark-gray sm:block">{rolePresentation[profile].subtitle}</p>{/if}
    </div>
    {#if crmEnabled && activities.isSuccess && progress.total}
      <div class="hidden items-center gap-4 rounded-[20px] bg-white px-5 py-4 shadow-sm sm:flex">
        <div class="flex h-[72px] w-[72px] items-center justify-center rounded-full" style="background: {ring}" role="img" aria-label="{progress.pct}% of today's touchpoints done">
          <span class="flex h-14 w-14 items-center justify-center rounded-full bg-white font-heading text-lg font-bold text-navy">{progress.pct}%</span>
        </div>
        <div>
          <p class="font-heading text-xl font-bold text-navy">{progress.done} of {progress.total} done</p>
          <p class="text-[13px] text-dark-gray">{progress.done === progress.total ? 'Day cleared — nice work.' : `${progress.total - progress.done} touchpoints still due`}</p>
        </div>
      </div>
    {/if}
  </section>

  {#if profile === 'sales' && crmEnabled}
    <!-- Phones: the summary sentence and progress ring become three tappable counts. -->
    <nav aria-label="Today at a glance" class="grid grid-cols-3 gap-2 sm:hidden">
      <button class="flex flex-col rounded-2xl bg-white p-3 text-left shadow-sm" onclick={() => route.push({ tab: 'activities' })}><span class="font-heading text-2xl font-bold leading-tight {overdue ? 'text-[#b23a26]' : 'text-navy'}">{agendaCounts.isSuccess ? overdue : '–'}</span><span class="text-xs text-dark-gray">Overdue</span></button>
      <button class="flex flex-col rounded-2xl bg-white p-3 text-left shadow-sm" onclick={() => route.push({ tab: 'activities' })}><span class="font-heading text-2xl font-bold leading-tight text-navy">{activities.isSuccess ? meetingsLeft : '–'}</span><span class="text-xs text-dark-gray">Meetings left</span></button>
      <button class="flex flex-col rounded-2xl bg-white p-3 text-left shadow-sm" onclick={() => scrollTo('my-day-followups')}><span class="font-heading text-2xl font-bold leading-tight text-navy">{activities.isSuccess && agendaRows.isSuccess ? `${progress.done}/${progress.total}` : '–'}</span><span class="text-xs text-dark-gray">Done today</span></button>
    </nav>
  {/if}


  {#if !enabled}<p role="alert">My day is available to CRM accounts. Open your assigned work from the navigation.</p>
  {:else}

    {#if profile === 'sales' && upcoming}
      {@const until = untilLabel(minutesUntil(upcoming.scheduled_at, now))}
      <section aria-label="Next up" class="day-card-dark next-up flex flex-wrap items-center gap-x-4 gap-y-4 sm:gap-x-8 sm:gap-y-5">
        <div class="flex h-[64px] w-[64px] shrink-0 flex-col items-center justify-center rounded-[14px] bg-[#1a3252] sm:h-[84px] sm:w-[84px] sm:rounded-[18px]">
          <span class="font-heading text-lg font-bold sm:text-2xl">{timeInIndia(upcoming.scheduled_at).replace(/\s?[ap]m$/i, '')}</span>
          <span class="text-[11px] text-[#c9d3de] sm:text-xs">{timeInIndia(upcoming.scheduled_at).match(/[ap]m$/i)?.[0] ?? ''}{until ? ` · ${until}` : ''}</span>
        </div>
        <div class="min-w-0 flex-[1_1_200px] sm:flex-[1_1_320px]">
          <p class="text-xs font-semibold uppercase tracking-[0.08em] text-toyama-lime">Next up · {upcoming.type.replace('_', ' ')}</p>
          <h2 class="mt-1 font-heading text-lg font-bold leading-snug sm:text-2xl">{upcoming.title || 'Meeting'}</h2>
          <p class="mt-1 truncate text-[13px] text-[#c9d3de] sm:whitespace-normal sm:text-sm">{[upcoming.location, bookedBy(upcoming.created_by)].filter(Boolean).join(' · ') || 'Assigned to you'}</p>
          {#if upcoming.body}<p class="mt-2 hidden line-clamp-2 sm:block text-[13px] text-[#e3e8ee]">{upcoming.body}</p>{/if}
        </div>
        <div class="flex w-full gap-2 sm:w-auto sm:flex-wrap sm:gap-2.5">
          {#if upcoming.location}<a class="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-xl sm:flex-none bg-toyama-lime px-4 font-semibold text-navy" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(upcoming.location)}`} target="_blank" rel="noopener noreferrer"><span class="hidden sm:inline">Get directions</span><span class="sm:hidden">Directions</span></a>{/if}
          <button class="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-xl border border-[#3a5578] px-4 text-white hover:bg-[#1a3252] sm:flex-none" onclick={() => route.push({ tab: 'activities', id: upcoming.id })}>Open<span class="hidden sm:inline">&nbsp;{upcoming.type === 'meeting' ? 'meeting' : 'touchpoint'}</span></button>
          {#if upcoming.person_id}<button class="inline-flex min-h-[44px] flex-1 items-center justify-center rounded-xl border border-[#3a5578] px-4 text-white hover:bg-[#1a3252]" onclick={() => route.push({ tab: 'people', id: upcoming.person_id! })}>Customer</button>{/if}
        </div>
      </section>
    {/if}

    {#if profile === 'coordination'}
      <nav aria-label="Your flow today" class="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
        <button class="day-tile" onclick={() => scrollTo('handover-title')}><span class="text-xs text-dark-gray">New enquiries</span><span class="day-tile-value">{leads.data?.count ?? '–'}</span><span class="text-xs text-dark-gray">Triage and hand over</span></button>
        <button class="day-tile !bg-navy" onclick={() => scrollTo('call-queue')}><span class="text-xs text-[#c9d3de]">Customer calls</span><span class="day-tile-value !text-white">{callCount}</span><span class="text-xs text-toyama-lime">{happy.data?.negatives?.length ? `${happy.data.negatives.length} unhappy — call first` : 'Happy calls, fixes, callbacks'}</span></button>
        <button class="day-tile" onclick={() => scrollTo('booked-meetings')}><span class="text-xs text-dark-gray">Meetings I booked today</span><span class="day-tile-value">{activities.isSuccess ? bookedToday.length : '–'}</span><span class="text-xs {bookedToday.some(row => !row.assignee_user_id) ? 'text-[#8a5300]' : 'text-dark-gray'}">{bookedToday.some(row => !row.assignee_user_id) ? 'Some need an attendee' : 'Attendees confirmed'}</span></button>
        <button class="day-tile" onclick={() => scrollTo('care-worklist')}><span class="text-xs text-dark-gray">Open issues</span><span class="day-tile-value">{care.data?.open_issues?.count ?? '–'}</span><span class="text-xs {quietCases.length ? 'text-[#b23a26]' : 'text-dark-gray'}">{quietCases.length ? `${quietCases.length} stalled in preview` : 'No stalled cases in preview'}</span></button>
      </nav>
      <HandoverPanel />
    {/if}


    {#if profile === 'sales'}
      <div class="grid items-start gap-5 xl:grid-cols-2">
        {@render followupCard()}
        <div class="space-y-5">
          <DailyMeetings {today} />
          <section class="day-card" aria-labelledby="sales-today">
            <h2 id="sales-today" class="day-title mb-3.5">Today</h2>
            {#if activities.isPending}<p role="status" class="day-meta">Loading your agenda…</p>
            {:else if agenda.length}
              <ol>
                {#each agenda as item, index (item.id)}
                  {@const past = Date.parse(item.scheduled_at!) < now.getTime()}
                  <li class="flex gap-3.5">
                    <span class="w-16 shrink-0 pt-0.5 text-[13px] {past ? 'text-dark-gray' : 'font-semibold text-navy'}">{timeInIndia(item.scheduled_at)}</span>
                    <span class="flex flex-col items-center" aria-hidden="true"><span class="mt-1 h-3 w-3 rounded-full {item.status === 'completed' ? 'bg-toyama-lime' : past ? 'border-[3px] border-toyama-teal bg-white' : 'bg-navy'}"></span>{#if index < agenda.length - 1}<span class="w-0.5 flex-1 bg-[#eeedea]"></span>{/if}</span>
                    <div class="min-w-0 pb-4">
                      <button class="text-left {item.status === 'completed' ? 'text-dark-gray line-through' : 'font-semibold text-navy'} hover:underline" onclick={() => route.push({ tab: 'activities', id: item.id })}>{item.title || touchpointTypeLabel(item.type)}</button>
                      <p class="text-xs text-dark-gray">{[touchpointTypeLabel(item.type), item.status === 'completed' ? 'Done' : past ? 'Started — record the outcome' : '', item.location, bookedBy(item.created_by)].filter(Boolean).join(' · ')}</p>
                    </div>
                  </li>
                {/each}
              </ol>
            {:else}<p class="rounded-xl bg-[#f7f7f4] p-3 text-sm text-dark-gray">No meetings or calls scheduled for you today.</p>{/if}
          </section>
          <OwnedSalesWork section="pipeline" />
        </div>
      </div>
      <OwnedSalesWork section="care" />
    {/if}

    {#if profile === 'coordination' || profile === 'care'}
      <div class="grid items-start gap-5 xl:grid-cols-2">
        <CallQueueCard />
        <DailyMeetings {today} />
      </div>
      {#if crmEnabled}{@render followupCard()}{/if}
      <!-- Customer care used to be a separate Dashboard tab; it is part of this one home now. -->
      {#key `${session.me?.user.id}:${accessKey}`}<CustomerCareDashboard embedded />{/key}
    {/if}

  {/if}

  {#if completing}
    <Composer activity={completing.activity} mode="log" onClose={cancelCompletion} />
  {/if}
</div>

{#snippet followupCard()}
  {#if crmEnabled}
    <section class="day-card" aria-labelledby="my-day-followups">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <h2 id="my-day-followups" class="day-title">{profile === 'sales' ? 'Due now' : 'Follow-ups due'}</h2>
        <div class="day-segment" role="group" aria-label="Touchpoint ownership">
          {#each [{ value: 'mine', label: 'Mine' }, { value: 'team', label: 'Team' }, { value: 'unassigned', label: 'Unassigned' }] as item}
            <button aria-pressed={scope === item.value} onclick={() => scopeChoice = { identity, scope: item.value as DayScope }}>{item.label}{#if agendaRows.isSuccess} · {queues[item.value as DayScope].length}{/if}</button>
          {/each}
        </div>
      </div>
      <div class="mb-2"></div>
      {#if agendaRows.isPending}<p role="status" class="day-meta">Loading your touchpoints…</p>
      {:else if agendaRows.isError}<p role="alert" class="text-sm text-red-700">{getErrorMessage(agendaRows.error)}</p><button class="day-mini-btn mt-2" onclick={() => agendaRows.refetch()}>Retry follow-ups</button>
      {:else}
        {#each followups.slice(0, 8) as work (work.activity.id)}
          {@const late = work.day < today}
          {@const lateDays = late ? daysSinceInIndia(work.day, today) : 0}
          <article class="day-row !items-start">
            {#if mayCompleteOwnTouchpoint(work, userId)}
              <button class="mt-0.5 h-7 w-7 shrink-0 rounded-full border-2 border-[#b9bdbe] bg-white hover:border-toyama-teal hover:bg-[#e3f0f1]" aria-label="Complete: {work.activity.title || work.activity.type}" onclick={() => startCompletion(work)}></button>
            {:else}<span class="mt-0.5 h-7 w-7 shrink-0 rounded-full border-2 border-dashed border-[#dbdbdb]" aria-hidden="true"></span>{/if}
            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                <button class="text-left font-semibold text-navy hover:underline" onclick={() => route.push({ tab: 'activities', id: work.activity.id })}>{work.kind === 'next_action' ? work.activity.next_action || work.activity.title || 'Follow-up' : work.activity.title || work.activity.type}</button>
                <span class="day-chip {late ? 'day-chip-warn' : 'day-chip-info'}">{late ? `Overdue${lateDays ? ` ${lateDays}d` : ''}` : 'Today'}</span>
              </div>
              <p class="mt-0.5 text-[13px] text-dark-gray">{touchpointTypeLabel(work.activity.type)} · {showDate(work)}</p>
              {#if scope === 'mine' && !mayCompleteOwnTouchpoint(work, userId) && work.activity.assignee_user_id !== userId}<p class="mt-1 hidden text-xs text-dark-gray sm:block">{work.activity.type==='email' ? 'Synced email — log the reply from the contact.' : 'Assigned to someone else.'}</p>{/if}
            </div>
          </article>
        {:else}<p class="rounded-xl bg-[#ecf8cc] p-3 text-sm text-[#2e4400]">Nothing due. Later follow-ups are in Agenda.</p>{/each}
        {#if followups.length > 8}<p class="mt-2 text-xs text-dark-gray">Showing 8 of {followups.length} due touchpoints.</p>{/if}
      {/if}
      <button class="day-link mt-3 inline-block" onclick={() => route.push({ tab: 'activities' })}>Open Agenda →</button>
    </section>
  {/if}
{/snippet}
